<template>
  <panel-layout
    id="resource-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #actions-right>
      <div v-if="resource && !resource.isNewRecord" class="flex items-center space-x-2">
        <o-history-link :item="resource" model="Employee" icon="history" slim flat />
      </div>
    </template>

    <template #heading>
      <div v-if="resource" class="flex items-center space-x-2">
        <o-avatar v-if="!resource.isNewRecord" :items="[ resource ]" size="lg" />
        <div>
          <h2 class="text-lg md:text-xl tracking-tight font-bold text-gray-800/85 leading-none dark:text-gray-100">
            {{ resource.isNewRecord ? $t('resource.new') : resource.displayName }}
          </h2>
          <div v-if="!resource.isNewRecord" class="mt-1">
            <p class="text-gray-700 text-sm md:text-base dark:text-gray-200" v-html="$t('pages.resource.edit.id', { id: resource.idEmployee })" />
          </div>
        </div>
      </div>
    </template>

    <div v-if="resource" class="h-full">
      <o-tabs
        ref="tabs"
        nav-class="bg-gray-50 h-11 border-b dark:bg-gray-900"
        scroll
      >
        <o-tab :name="$t('general.details')" icon="customerEdit">
          <resource-details v-model:resource="resource" />
        </o-tab>
        <o-tab :name="$t('general.resourceGroups')" icon="resources">
          <o-form ref="form">
            <resource-types :resource-types="resourceTypes" v-model:resource="resource" />
          </o-form>
        </o-tab>
        <!--<o-tab name="Roster" icon="roster">
          <resource-roster v-model:resource="resource" />
        </o-tab>-->

        <o-tab :is-visible="!resource.isNewRecord && !!roster && resource.resourceRostered" :name="$t('general.roster')" icon="roster">
          <resource-roster v-model:resource="resource" v-model:roster="roster" />
        </o-tab>
      </o-tabs>
    </div>

    <template #footer>
      <div v-if="resource">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button
              :disabled="!unsavedChanges"
              variant="primary"
              outline
              icon="save"
              class="mr-2"
              @click="save()"
            >
              {{ $t('general.save') }}
            </o-button>
            <o-button :disabled="!unsavedChanges" variant="primary" icon="save" class="mr-2" @click="save(true)">
              {{ $t('general.saveAndClose') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
import ResourceDetails from '@/components/resource/edit/Details.vue'
import ResourceTypes from '@/components/resource/edit/ResourceTypes.vue'
import ResourceRoster from '@/components/resource/edit/Roster.vue'

// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

import {
  OTabs,
  OTab,
  OAlert,
  OAvatar,
} from '@/components/elements'

import { EmployeeType } from '~/_api/models/employee'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const props = defineProps<{
  id?: string
  resourceType?: string
  tab?: string
}>()

const { $api, $notification, $bus, $i18n } = useNuxtApp()
const router = useRouter()

const resourceTypes = ref<any[]>([])
const resource = ref<any>(null)
const roster = ref<any>(null)
const loading = ref(true)

const tabs = ref<any>()
const confirmDialog = ref<any>()

const { unsavedChanges: resourceChanged, snapshot: snapshotResource } = useUnsavedChanges(resource)
const { unsavedChanges: rosterChanged, snapshot: snapshotRoster } = useUnsavedChanges(roster)

const unsavedChanges = computed(() => {
  return resourceChanged.value || rosterChanged.value
})

const fetchResourceTypes = async () => {
  try {
    const response = await $api.resourceType.list()
    resourceTypes.value = response.items
  } catch (e) {
    console.log(e)
  }
}

const fetchResource = async () => {
  loading.value = true

  try {
    if (!props.id) {
      resource.value = (await $api.employee.defaults({} as any)).data
      resource.value.type = EmployeeType.Resource

      if (props.resourceType) {
        resource.value.resourceTypes = resourceTypes.value
          .filter(({ idResourceType }: any) => idResourceType === Number(props.resourceType))
      }
    } else {
      resource.value = (await $api.employee.get(props.id)).data
    }
    snapshotResource()
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

const fetchRoster = async () => {
  loading.value = true

  try {
    if (props.id) {
      roster.value = (await $api.roster.employeeDefault({ idEmployee: props.id } as any)).data
    }
    snapshotRoster()
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

const save = async (close = false) => {
  loading.value = true

  let _resource = resource.value
  let _roster = roster.value

  const isNewRecord = resource.value.isNewRecord

  try {
    if (resourceChanged.value) {
      _resource = (await $api.employee.save(resource.value)).data
    }

    if (rosterChanged.value) {
      const success = await $api.roster.saveEmployeeDefault(_resource, _roster)
      _roster = !success ? false : _roster
    }

    if (_resource && _roster) {
      snapshotResource()
      snapshotRoster()

      $notification.toast({
        title: $i18n.t('pages.resource.edit.success'),
        text: $i18n.t('pages.resource.edit.successInfo'),
        variant: 'success',
      })

      $bus.emit.updated('Employee', _resource)
      $bus.emit.updated('Roster', _roster)

      if (close) {
        panel.close()
      }
    } else if (_resource && isNewRecord) {
      snapshotResource()

      $notification.toast({
        title: $i18n.t('pages.resource.edit.createSuccess'),
        text: $i18n.t('pages.resource.edit.successInfo'),
        variant: 'success',
      })

      $bus.emit.updated('Employee', _resource)

      if (close) {
        panel.close(true)
      } else {
        // Swap in-place to the newly-created resource's edit view.
        panel.replace('resource/edit', { id: String(_resource.idEmployee) })
      }
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

watch(() => props.id, async () => {
  loading.value = true
  await fetchResourceTypes()
  await fetchResource()
  await fetchRoster()

  if (props.tab) {
    tabs.value?.selectTab('#' + props.tab)
  }

  loading.value = false
}, { immediate: true })
</script>
